import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeHeading, setActiveHeading] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = article
    ? articles.filter((a) => article.relatedArticles.includes(a.slug))
    : [];

  useEffect(() => {
    if (!article) return;
    const handleScroll = () => {
      const headings = document.querySelectorAll('h2[id]');
      let current = '';
      headings.forEach((h) => {
        const rect = h.getBoundingClientRect();
        if (rect.top <= 120) current = h.id;
      });
      setActiveHeading(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const headings = article.content
    .filter((s) => s.type === 'heading')
    .map((s) => ({ id: slugify(s.text || ''), text: s.text || '' }));

  function slugify(text: string): string {
    return text
      .replace(/[，。、；：！？（）【】《》""'']/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase()
      .replace(/[^a-z0-9\u4e00-\u9fff-]/g, '')
      .substring(0, 50);
  }

  return (
    <>
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        datePublished: article.publishedAt,
        author: { '@type': 'Organization', name: '强奸罪法律解析与辩护指南' },
        publisher: { '@type': 'Organization', name: '强奸罪法律解析与辩护指南' },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin + '/' : '' },
          { '@type': 'ListItem', position: 2, name: article.category, item: typeof window !== 'undefined' ? window.location.origin + '/category/' + article.categorySlug : '' },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />

      <Navbar />
      <main className="pt-20">
        <Breadcrumb items={[
          { label: article.category, href: `/category/${article.categorySlug}` },
          { label: article.title },
        ]} />

        <article className="py-10 md:py-14">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
            <div className="flex gap-10">
              {/* Sidebar - Table of Contents */}
              <aside className="hidden lg:block w-56 flex-shrink-0">
                <div className="sticky top-24">
                  <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-3">目录导航</h4>
                  <nav className="space-y-1">
                    {headings.map((h) => (
                      <a
                        key={h.id}
                        href={`#${h.id}`}
                        className={`block text-xs py-1.5 px-2 rounded transition-colors whitespace-nowrap ${
                          activeHeading === h.id
                            ? 'text-primary-600 bg-primary-50 font-medium'
                            : 'text-foreground-500 hover:text-foreground-800'
                        }`}
                      >
                        {h.text}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0 max-w-3xl">
                <div className="mb-6">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium mb-4">
                    {article.category}
                  </span>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 mb-3 leading-tight">
                    {article.title}
                  </h1>
                  <div className="flex items-center gap-3 text-xs text-foreground-400 mb-4">
                    <span>{article.publishedAt}</span>
                    <span>·</span>
                    <span>{article.readTime}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs border border-background-200">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="prose-custom text-sm leading-relaxed text-foreground-800 space-y-5">
                  {article.content.map((section, idx) => {
                    if (section.type === 'heading') {
                      const id = slugify(section.text || '');
                      const HeadingTag = `h${section.level || 2}` as keyof JSX.IntrinsicElements;
                      const sizeClass = section.level === 2
                        ? 'text-lg md:text-xl font-heading font-bold text-foreground-950 mt-8 mb-4'
                        : 'text-base md:text-lg font-heading font-semibold text-foreground-900 mt-6 mb-3';
                      return (
                        <HeadingTag key={idx} id={id} className={sizeClass}>
                          {section.text}
                        </HeadingTag>
                      );
                    }
                    if (section.type === 'paragraph') {
                      return <p key={idx} className="text-foreground-700">{section.text}</p>;
                    }
                    if (section.type === 'blockquote') {
                      return (
                        <blockquote key={idx} className="border-l-3 border-primary-400 bg-primary-50/50 px-4 py-3 text-foreground-600 italic rounded-r-md">
                          {section.text}
                        </blockquote>
                      );
                    }
                    if (section.type === 'list') {
                      return (
                        <ul key={idx} className="list-disc pl-5 space-y-1 text-foreground-700">
                          {section.items?.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      );
                    }
                    return null;
                  })}
                </div>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                  <div className="mt-12 pt-8 border-t border-background-200">
                    <h3 className="font-heading font-semibold text-foreground-950 mb-4">相关文章</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {relatedArticles.map((ra) => (
                        <Link
                          key={ra.slug}
                          to={`/article/${ra.slug}`}
                          className="p-4 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                        >
                          <h4 className="text-sm font-medium text-foreground-900 mb-1 hover:text-primary-600 transition-colors line-clamp-2">
                            {ra.title}
                          </h4>
                          <span className="text-xs text-foreground-400">{ra.readTime}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}